import React, { useState, useMemo } from 'react';
import { Copy, Check, Smartphone, ArrowUpRight, ArrowDownLeft, Send, Plus, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { usePlatform } from '../../context/PlatformContext';

const PRESET_COLORS = ['#E8678B', '#6366F1', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#000000'];

function PhoneMockup({ config }) {
  const { brandColor, appName, logoUrl, darkMode, cornerRadius } = config;
  const bg = darkMode ? '#111827' : '#FFFFFF';
  const cardBg = darkMode ? '#1F2937' : '#F9FAFB';
  const textPrimary = darkMode ? '#FFFFFF' : '#111827';
  const textSecondary = darkMode ? '#9CA3AF' : '#6B7280';
  const borderColor = darkMode ? '#374151' : '#E5E7EB';
  const radius = cornerRadius === 'sharp' ? '4px' : cornerRadius === 'pill' ? '24px' : '12px';
  const btnRadius = cornerRadius === 'sharp' ? '4px' : cornerRadius === 'pill' ? '9999px' : '10px';

  const transactions = [
    { name: 'Top-up via bank', amount: '+₦25,000', type: 'credit', time: '2 min ago' },
    { name: 'Transfer to Kemi B.', amount: '-₦5,000', type: 'debit', time: '1 hr ago' },
    { name: 'Deposit from VA', amount: '+₦10,000', type: 'credit', time: '3 hrs ago' },
  ];

  return (
    <div className="relative mx-auto" style={{ width: 300, height: 580 }}>
      {/* Phone frame */}
      <div className="absolute inset-0 rounded-[36px] border-[6px] border-gray-800 bg-gray-800 shadow-2xl overflow-hidden">
        {/* Status bar */}
        <div className="flex items-center justify-between px-6 pt-2 pb-1" style={{ background: brandColor }}>
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 600 }}>9:41</span>
          <div className="flex gap-1">
            <div className="w-4 h-2 rounded-sm bg-white/80" />
            <div className="w-1 h-2 rounded-sm bg-white/50" />
          </div>
        </div>

        {/* App header */}
        <div className="px-5 pt-3 pb-4" style={{ background: brandColor }}>
          <div className="flex items-center gap-2 mb-3">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="w-7 h-7 rounded-lg object-cover" onError={e => { e.target.style.display = 'none'; }} />
            ) : (
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)' }}>
                <span className="text-white font-bold text-xs">{appName?.[0] || 'A'}</span>
              </div>
            )}
            <span className="text-white font-semibold text-sm">{appName || 'My App'}</span>
          </div>
          <p className="text-white/70 text-xs mb-1">Available Balance</p>
          <p className="text-white text-2xl font-bold tracking-tight">₦ 125,400.00</p>
        </div>

        {/* Body */}
        <div style={{ background: bg, height: '100%' }} className="px-4 pt-4">
          {/* Action buttons */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            {[
              { icon: Plus, label: 'Add Money' },
              { icon: Send, label: 'Transfer' },
              { icon: Eye, label: 'Details' },
            ].map(a => (
              <button key={a.label} className="flex flex-col items-center gap-1.5 py-3" style={{ background: cardBg, borderRadius: btnRadius, border: `1px solid ${borderColor}` }}>
                <a.icon size={16} style={{ color: brandColor }} />
                <span style={{ color: textPrimary, fontSize: 10, fontWeight: 500 }}>{a.label}</span>
              </button>
            ))}
          </div>

          {/* Transactions */}
          <p style={{ color: textPrimary, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Recent Activity</p>
          <div className="space-y-0" style={{ borderRadius: radius, border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
            {transactions.map((tx, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2.5" style={{ background: cardBg, borderBottom: i < 2 ? `1px solid ${borderColor}` : 'none' }}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: tx.type === 'credit' ? '#DCFCE7' : '#FEE2E2' }}>
                    {tx.type === 'credit' ? <ArrowDownLeft size={12} className="text-green-600" /> : <ArrowUpRight size={12} className="text-red-500" />}
                  </div>
                  <div>
                    <p style={{ color: textPrimary, fontSize: 11, fontWeight: 500 }}>{tx.name}</p>
                    <p style={{ color: textSecondary, fontSize: 9 }}>{tx.time}</p>
                  </div>
                </div>
                <span style={{ color: tx.type === 'credit' ? '#16A34A' : '#EF4444', fontSize: 11, fontWeight: 600 }}>{tx.amount}</span>
              </div>
            ))}
          </div>

          {/* Powered by */}
          <p className="text-center mt-4" style={{ color: textSecondary, fontSize: 8 }}>
            Powered by Pebbles
          </p>
        </div>
      </div>
    </div>
  );
}

function CodeSnippet({ code, title }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="bg-gray-900 rounded-xl overflow-hidden" data-testid={`snippet-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800">
        <span className="text-xs text-gray-400">{title}</span>
        <button onClick={handleCopy} className="text-gray-400 hover:text-white transition-colors">
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <pre className="p-4 text-xs text-gray-300 overflow-x-auto leading-relaxed"><code>{code}</code></pre>
    </div>
  );
}

export default function PlatformWalletConfig() {
  const { tenant } = usePlatform();
  const [config, setConfig] = useState({
    brandColor: '#E8678B',
    appName: tenant.name || 'My App',
    logoUrl: '',
    darkMode: false,
    cornerRadius: 'rounded',
  });
  const [activeTab, setActiveTab] = useState('preview');

  const update = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  const embedUrl = useMemo(() => {
    const base = 'https://www.pebbles.financial';
    const params = new URLSearchParams({
      color: config.brandColor,
      name: config.appName,
      dark: config.darkMode ? '1' : '0',
      radius: config.cornerRadius,
      ...(config.logoUrl && { logo: config.logoUrl }),
    });
    return `${base}/platform/embed/wallet?${params.toString()}`;
  }, [config]);

  const reactSnippet = `import { PebblesWallet } from '@pebbles/react';

<PebblesWallet
  apiKey={process.env.PEBBLES_API_KEY}
  userId="your_user_id"
  theme={{
    brandColor: "${config.brandColor}",
    appName: "${config.appName}",
    darkMode: ${config.darkMode},
    cornerRadius: "${config.cornerRadius}",${config.logoUrl ? `\n    logoUrl: "${config.logoUrl}",` : ''}
  }}
/>`;

  const iframeSnippet = `<iframe
  src="${embedUrl}"
  width="400"
  height="600"
  frameborder="0"
  allow="clipboard-write"
  style="border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.08);"
></iframe>`;

  const configJson = JSON.stringify({
    brandColor: config.brandColor,
    appName: config.appName,
    darkMode: config.darkMode,
    cornerRadius: config.cornerRadius,
    ...(config.logoUrl && { logoUrl: config.logoUrl }),
  }, null, 2);

  return (
    <div data-testid="platform-wallet-config-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Customize Wallet</h1>
        <p className="text-gray-500 mt-1">Configure how the wallet looks inside your app for your users</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left: Controls */}
        <div className="lg:w-[380px] space-y-5 shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
            {/* App Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">App Name</label>
              <input type="text" value={config.appName} onChange={e => update('appName', e.target.value)}
                data-testid="config-app-name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                placeholder="Your app name" />
            </div>

            {/* Brand Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Brand Color</label>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {PRESET_COLORS.map(c => (
                  <button key={c} onClick={() => update('brandColor', c)}
                    data-testid={`color-${c.replace('#', '')}`}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${config.brandColor === c ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                    style={{ background: c }} />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input type="color" value={config.brandColor} onChange={e => update('brandColor', e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border-0" />
                <input type="text" value={config.brandColor} onChange={e => update('brandColor', e.target.value)}
                  data-testid="config-brand-color"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]" />
              </div>
            </div>

            {/* Logo URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Logo URL (optional)</label>
              <input type="url" value={config.logoUrl} onChange={e => update('logoUrl', e.target.value)}
                data-testid="config-logo-url"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                placeholder="https://your-cdn.com/logo.png" />
            </div>

            {/* Corner Radius */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Corner Style</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'sharp', label: 'Sharp' },
                  { value: 'rounded', label: 'Rounded' },
                  { value: 'pill', label: 'Pill' },
                ].map(opt => (
                  <button key={opt.value} onClick={() => update('cornerRadius', opt.value)}
                    data-testid={`corner-${opt.value}`}
                    className={`py-2 text-xs font-medium rounded-lg border transition-colors ${
                      config.cornerRadius === opt.value
                        ? 'border-[#E8678B] bg-pink-50 text-[#E8678B]'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dark Mode */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Dark Mode</label>
              <button onClick={() => update('darkMode', !config.darkMode)}
                data-testid="config-dark-mode"
                className={`relative w-11 h-6 rounded-full transition-colors ${config.darkMode ? 'bg-[#E8678B]' : 'bg-gray-300'}`}>
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.darkMode ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>

          {/* Save / Export */}
          <button onClick={() => { navigator.clipboard.writeText(configJson); toast.success('Config JSON copied!'); }}
            data-testid="copy-config-btn"
            className="w-full py-2.5 bg-[#E8678B] text-white rounded-lg text-sm font-semibold hover:bg-[#d15a7d] transition-colors">
            Copy Configuration JSON
          </button>
        </div>

        {/* Right: Preview + Snippets */}
        <div className="flex-1 min-w-0">
          {/* Tab bar */}
          <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit" data-testid="config-tabs">
            {[
              { key: 'preview', label: 'Live Preview' },
              { key: 'react', label: 'React Snippet' },
              { key: 'iframe', label: 'Iframe Embed' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'preview' && (
            <div className="flex items-start justify-center pt-4" data-testid="phone-preview">
              <PhoneMockup config={config} />
            </div>
          )}

          {activeTab === 'react' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Drop this component into your React app. Install the SDK first:</p>
              <CodeSnippet title="Install" code="npm install @pebbles/react" />
              <CodeSnippet title="React Component" code={reactSnippet} />
            </div>
          )}

          {activeTab === 'iframe' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Embed the wallet in any website with an iframe. No SDK needed.</p>
              <CodeSnippet title="Iframe Embed" code={iframeSnippet} />
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm font-medium text-gray-900 mb-2">Your Embed URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-50 px-3 py-2 rounded-lg text-xs font-mono text-gray-600 border truncate" data-testid="embed-url-display">{embedUrl}</code>
                  <button onClick={() => { navigator.clipboard.writeText(embedUrl); toast.success('URL copied!'); }}
                    data-testid="copy-embed-url-btn"
                    className="px-3 py-2 bg-[#E8678B] text-white rounded-lg hover:bg-[#d15a7d] transition-colors shrink-0">
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
